import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useUserQuery } from '@/queries/useUserQuery'
import { User as UserType } from '@/types/User'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import DadosMestreHeader from '../components/DadosMestreHeader' 
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions'  
import DefaultColumn from '@/components/Table/components/DefaultColumn' 
import TableRowActions from '@/components/Table/components/TableRowActions'
import DeleteDialog from './components/DeleteDialog'
import EditDialog from './components/EditDialog'
import ResetPasswordDialog from './components/ResetPasswordDialog'
import AddDialog from './components/AddDialog'
import { useTable } from '@/hooks/useTable' 
import useDisclosure from '@/hooks/useDisclosure'
import { Key, User as UserIcon } from 'lucide-react'
import { TypographyH1, TypographyMuted } from '@/components/ui/typography'

export type UserTable = TableData<UserType>

const columnHelper = createColumnHelper<UserType>()

const User = () => {
    const {
        table, 
        selectedRow,
        selectedRows,
        isAddDialogOpen,
        isDeleteDialogOpen,
        isEditDialogOpen, 
        onAddDialogClose,
        onClickAdd,
        onClickDelete,
        onClickEdit,
        onClickMultiDelete,
        onDeleteDialogClose,
        onEditDialogClose, 
        setTable,
        setSelectedRow
    } = useTable<UserType>('')
 
     const {
        isOpen: isResetPasswordDialogOpen,
        onClose: onResetPasswordDialogClose,
        onOpen: onResetPasswordDialogOpen
    } = useDisclosure()

    const { data, isFetching, isPending, refetch } = useUserQuery()

    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [  
            columnHelper.accessor('NM_USUARIO', {
                id: 'NM_USUARIO',
                header: 'Nome',
                size: 250,
            }), 
            columnHelper.accessor('DS_USUARIO', {
                id: 'DS_USUARIO',
                header: 'Usuário',
                size: 140,
            }), 
            columnHelper.accessor('DD_EMAIL', {
                id: 'DD_EMAIL',
                header: 'E-mail',
                size: 280,
            }), 
            columnHelper.accessor('DD_NUMERO_CONTATO', {
                id: 'DD_NUMERO_CONTATO',
                header: 'Num. Contato',
                size: 140,
            }),  
            columnHelper.accessor(({ DD_PERFIL }) =>
                DD_PERFIL.charAt(0).toUpperCase() + DD_PERFIL.slice(1).toLowerCase() , { 
                id: 'DD_PERFIL',
                header: 'Perfil',
                size: 110,
            }),  
            columnHelper.accessor(({ DD_STATUS }) =>
                DD_STATUS
                    ? 'Ativo'
                    : 'Inativo', { 
                id: 'DD_STATUS',
                header: 'Status',
                size: 90,
            }), 
            columnHelper.display({
                id: 'Ações',
                cell: ({ row }) => { 
                    return (
                        (
                            <TableRowActions
                                onClickDelete={() => onClickDelete(row)}
                                onClickEdit={() => onClickEdit(row)}
                                actions={[{
                                    label: 'Resetar Senha',
                                    icon: <Key size={14}/>,
                                    action: () => { setSelectedRow(row); onResetPasswordDialogOpen()}
                                }]}
                            />
                        )
                    )
                },
                size: 60,
                enableColumnFilter: false,
                enableSorting: false,
            }),
        ],
        []
    )

    return (
        <div className="w-full h-full p-4 overflow-hidden">
            <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                        <UserIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <TypographyH1>
                            Usuários
                        </TypographyH1>
                        <TypographyMuted>
                            Cadastre e efetue manutenções dos usuários
                        </TypographyMuted>
                    </div>
                </div>
            </div>
            
            <Table<UserTable>
                data={memoData}
                columns={memoColumns}
                getRowId={(row) => row.SK_USUARIO.toString()}
                isLoading={isPending}
                isFetching={isFetching}
                getTableInstance={(table) => setTable(table)}    
                tableHeader={
                    <DadosMestreHeader
                        title={''}
                        onDelete={onClickMultiDelete}
                        selectedRows={
                            table
                                ?.getFilteredRowModel()
                                .flatRows.filter((row) => selectedRows[row.id])
                                .length
                        }
                    />
                }
                tableActions={
                    <DadosMestreHeaderActions 
                        onRefech={refetch}
                        onAdd={onClickAdd}
                    />
                } 
                defaultColumn={{
                    cell: ({ getValue }) => (
                        <DefaultColumn>{getValue() as string}</DefaultColumn>
                    ),
                }}
            /> 

            {(isDeleteDialogOpen && selectedRow)  && (
                <DeleteDialog
                    ids={ selectedRow.original.SK_USUARIO }
                    nameUser={selectedRow.original.NM_USUARIO}
                    isOpen={isDeleteDialogOpen}
                    onClose={onDeleteDialogClose}
                />
            )}
            {selectedRow && (
                <EditDialog
                    row={selectedRow}
                    isOpen={isEditDialogOpen}
                    onClose={onEditDialogClose}
                />
            )} 
            {selectedRow && (
                <ResetPasswordDialog
                    row={selectedRow}
                    isOpen={isResetPasswordDialogOpen}
                    onClose={onResetPasswordDialogClose}
                />
            )} 
            <AddDialog isOpen={isAddDialogOpen} onClose={onAddDialogClose} />
        </div>
    )
}

export default User
